import { useMemo, useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { GitBranch, ListTree, Pencil, Plus, Save, Sparkles, Trash2, Zap } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { friendlyMessage } from '@/api/http'
import {
  subplotApi,
  type ChapterStorylineWeightInput,
  type Storyline,
  type StorylineInput,
  type Subplot,
  type SubplotInput,
} from '@/api/subplotApi'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { NumberInput } from '@/components/ui/number-input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import type { Chapter, Project } from '@/types/nf'

type StorylineFormValues = {
  name: string
  line_type: string
  priority: number
  arc_summary: string
  status: string
}

type SubplotFormValues = {
  storyline_id: string
  title: string
  description: string
  dig_chapter_number: number | undefined
  target_detonate_chapter_number: number | undefined
  convergence_chapter_number: number | undefined
  importance: string
  remind_before_chapters: number | undefined
  include_in_context: boolean
}

type WeightDraft = Record<string, { weight: string; is_convergence_node: boolean }>

const emptyStoryline: StorylineFormValues = {
  name: '',
  line_type: 'main',
  priority: 5,
  arc_summary: '',
  status: 'active',
}

const emptySubplot: SubplotFormValues = {
  storyline_id: '',
  title: '',
  description: '',
  dig_chapter_number: undefined,
  target_detonate_chapter_number: undefined,
  convergence_chapter_number: undefined,
  importance: '0.5',
  remind_before_chapters: 3,
  include_in_context: true,
}

function toStorylineInput(values: StorylineFormValues): StorylineInput {
  return {
    name: values.name.trim(),
    line_type: values.line_type,
    priority: values.priority,
    arc_summary: values.arc_summary.trim() || null,
    status: values.status,
  }
}

function toSubplotInput(values: SubplotFormValues): SubplotInput {
  return {
    storyline_id: values.storyline_id || null,
    title: values.title.trim(),
    description: values.description.trim() || null,
    dig_chapter_number: values.dig_chapter_number ?? null,
    target_detonate_chapter_number: values.target_detonate_chapter_number ?? null,
    convergence_chapter_number: values.convergence_chapter_number ?? null,
    importance: Number(values.importance) || 0,
    remind_before_chapters: values.remind_before_chapters ?? 0,
    include_in_context: values.include_in_context,
  }
}

function storylineValues(item: Storyline): StorylineFormValues {
  return {
    name: item.name,
    line_type: item.line_type || 'main',
    priority: item.priority,
    arc_summary: item.arc_summary ?? '',
    status: item.status || 'active',
  }
}

function subplotValues(item: Subplot): SubplotFormValues {
  return {
    storyline_id: item.storyline_id ?? '',
    title: item.title,
    description: item.description ?? '',
    dig_chapter_number: item.dig_chapter_number ?? undefined,
    target_detonate_chapter_number: item.target_detonate_chapter_number ?? undefined,
    convergence_chapter_number: item.convergence_chapter_number ?? undefined,
    importance: String(item.importance ?? 0.5),
    remind_before_chapters: item.remind_before_chapters ?? undefined,
    include_in_context: item.include_in_context,
  }
}

function Field({
  id,
  label,
  value,
  disabled,
  multiline,
  onChange,
}: {
  id: string
  label: string
  value: string
  disabled?: boolean
  multiline?: boolean
  onChange: (value: string) => void
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-xs text-muted-foreground">
        {label}
      </Label>
      {multiline ? (
        <textarea
          id={id}
          value={value}
          disabled={disabled}
          rows={3}
          onChange={(event) => onChange(event.target.value)}
          className="min-h-20 w-full resize-y rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none transition-[color,box-shadow] placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/30"
        />
      ) : (
        <Input id={id} value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)} />
      )}
    </div>
  )
}

export function StorylinesPanel({ project, chapter }: { project: Project; chapter?: Chapter | null }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const chapterNumber = chapter?.chapter_number ?? 1
  const storylinesKey = useMemo(() => ['storylines', project.id] as const, [project.id])
  const subplotsKey = useMemo(() => ['subplots', project.id] as const, [project.id])
  const weightsKey = useMemo(() => ['storyline-weights', project.id, chapterNumber] as const, [project.id, chapterNumber])
  const [storylineDialog, setStorylineDialog] = useState(false)
  const [subplotDialog, setSubplotDialog] = useState(false)
  const [editingStoryline, setEditingStoryline] = useState<Storyline | null>(null)
  const [editingSubplot, setEditingSubplot] = useState<Subplot | null>(null)
  const [storylineForm, setStorylineForm] = useState<StorylineFormValues>(emptyStoryline)
  const [subplotForm, setSubplotForm] = useState<SubplotFormValues>(emptySubplot)
  const [weightDraft, setWeightDraft] = useState<WeightDraft>({})

  const storylinesQuery = useQuery({
    queryKey: storylinesKey,
    queryFn: () => subplotApi.listStorylines(project.id),
  })
  const subplotsQuery = useQuery({
    queryKey: subplotsKey,
    queryFn: () => subplotApi.listSubplots(project.id),
  })
  const weightsQuery = useQuery({
    queryKey: weightsKey,
    queryFn: () => subplotApi.listWeights(project.id, chapterNumber),
  })

  const storylines = storylinesQuery.data ?? []
  const subplots = subplotsQuery.data ?? []
  const weights = weightsQuery.data ?? []
  const weightsByStoryline = useMemo(() => {
    const map = new Map(weights.map((item) => [item.storyline_id, item]))
    return map
  }, [weights])

  const invalidatePlanning = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: storylinesKey }),
      queryClient.invalidateQueries({ queryKey: subplotsKey }),
      queryClient.invalidateQueries({ queryKey: weightsKey }),
      queryClient.invalidateQueries({ queryKey: ['story-state', project.id] }),
    ])
  }

  const createStorylineMutation = useMutation({
    mutationFn: (body: StorylineInput) => subplotApi.createStoryline(project.id, body),
    onSuccess: async () => {
      await invalidatePlanning()
      toast.success(t('storylines.messages.storylineCreated'))
      setStorylineDialog(false)
    },
    onError: (error) => toast.error(friendlyMessage(error, t('storylines.messages.saveFailed'))),
  })
  const updateStorylineMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: StorylineInput }) => subplotApi.updateStoryline(project.id, id, body),
    onSuccess: async () => {
      await invalidatePlanning()
      toast.success(t('storylines.messages.storylineUpdated'))
      setStorylineDialog(false)
    },
    onError: (error) => toast.error(friendlyMessage(error, t('storylines.messages.saveFailed'))),
  })
  const deleteStorylineMutation = useMutation({
    mutationFn: (id: string) => subplotApi.deleteStoryline(project.id, id),
    onSuccess: async () => {
      await invalidatePlanning()
      toast.success(t('storylines.messages.storylineDeleted'))
    },
    onError: (error) => toast.error(friendlyMessage(error, t('storylines.messages.deleteFailed'))),
  })
  const createSubplotMutation = useMutation({
    mutationFn: (body: SubplotInput) => subplotApi.createSubplot(project.id, body),
    onSuccess: async () => {
      await invalidatePlanning()
      toast.success(t('storylines.messages.subplotCreated'))
      setSubplotDialog(false)
    },
    onError: (error) => toast.error(friendlyMessage(error, t('storylines.messages.saveFailed'))),
  })
  const updateSubplotMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: SubplotInput }) => subplotApi.updateSubplot(project.id, id, body),
    onSuccess: async () => {
      await invalidatePlanning()
      toast.success(t('storylines.messages.subplotUpdated'))
      setSubplotDialog(false)
    },
    onError: (error) => toast.error(friendlyMessage(error, t('storylines.messages.saveFailed'))),
  })
  const deleteSubplotMutation = useMutation({
    mutationFn: (id: string) => subplotApi.deleteSubplot(project.id, id),
    onSuccess: async () => {
      await invalidatePlanning()
      toast.success(t('storylines.messages.subplotDeleted'))
    },
    onError: (error) => toast.error(friendlyMessage(error, t('storylines.messages.deleteFailed'))),
  })
  const fillMutation = useMutation({
    mutationFn: (id: string) => subplotApi.fillSubplot(project.id, id),
    onSuccess: async () => {
      await invalidatePlanning()
      toast.success(t('storylines.messages.subplotFilling'))
    },
    onError: (error) => toast.error(friendlyMessage(error, t('storylines.messages.transitionFailed'))),
  })
  const detonateMutation = useMutation({
    mutationFn: ({ id, chapterNo }: { id: string; chapterNo: number }) => subplotApi.detonateSubplot(project.id, id, chapterNo),
    onSuccess: async () => {
      await invalidatePlanning()
      toast.success(t('storylines.messages.subplotDetonated'))
    },
    onError: (error) => toast.error(friendlyMessage(error, t('storylines.messages.transitionFailed'))),
  })
  const weightsMutation = useMutation({
    mutationFn: (body: ChapterStorylineWeightInput[]) => subplotApi.replaceWeights(project.id, chapterNumber, body),
    onSuccess: async (response) => {
      await invalidatePlanning()
      const message = response.validation.warnings.length
        ? response.validation.warnings.join(' ')
        : t('storylines.messages.weightsSaved')
      toast.success(message)
    },
    onError: (error) => toast.error(friendlyMessage(error, t('storylines.messages.weightsFailed'))),
  })

  const openStorylineCreate = () => {
    setEditingStoryline(null)
    setStorylineForm(emptyStoryline)
    setStorylineDialog(true)
  }

  const openStorylineEdit = (item: Storyline) => {
    setEditingStoryline(item)
    setStorylineForm(storylineValues(item))
    setStorylineDialog(true)
  }

  const openSubplotCreate = (storylineId?: string) => {
    setEditingSubplot(null)
    setSubplotForm({ ...emptySubplot, storyline_id: storylineId ?? storylines[0]?.id ?? '' })
    setSubplotDialog(true)
  }

  const openSubplotEdit = (item: Subplot) => {
    setEditingSubplot(item)
    setSubplotForm(subplotValues(item))
    setSubplotDialog(true)
  }

  const submitStoryline = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!storylineForm.name.trim()) return
    const body = toStorylineInput(storylineForm)
    if (editingStoryline) updateStorylineMutation.mutate({ id: editingStoryline.id, body })
    else createStorylineMutation.mutate(body)
  }

  const submitSubplot = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!subplotForm.title.trim()) return
    const body = toSubplotInput(subplotForm)
    if (editingSubplot) updateSubplotMutation.mutate({ id: editingSubplot.id, body })
    else createSubplotMutation.mutate(body)
  }

  const saveWeights = () => {
    const body = storylines.map((line) => {
      const existing = weightsByStoryline.get(line.id)
      const draft = weightDraft[line.id]
      return {
        storyline_id: line.id,
        weight: Number(draft?.weight ?? existing?.weight ?? 0),
        is_convergence_node: draft?.is_convergence_node ?? existing?.is_convergence_node ?? false,
      }
    })
    weightsMutation.mutate(body)
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold tracking-normal">
            <ListTree className="size-5 text-primary" />
            {t('storylines.title')}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">{t('storylines.subtitle')}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" size="sm" onClick={openStorylineCreate}>
            <Plus className="size-4" />
            {t('storylines.newStoryline')}
          </Button>
          <Button type="button" size="sm" onClick={() => openSubplotCreate()} disabled={!storylines.length}>
            <Plus className="size-4" />
            {t('storylines.newSubplot')}
          </Button>
        </div>
      </div>

      {storylinesQuery.isLoading || subplotsQuery.isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-40 rounded-lg" />
          <Skeleton className="h-40 rounded-lg" />
        </div>
      ) : storylinesQuery.error || subplotsQuery.error ? (
        <div className="rounded-lg border border-dashed bg-background/45 p-5 text-sm text-muted-foreground">
          {friendlyMessage(storylinesQuery.error ?? subplotsQuery.error, t('storylines.loadFailed'))}
        </div>
      ) : storylines.length === 0 ? (
        <div className="rounded-lg border border-dashed bg-background/45 p-5 text-sm text-muted-foreground">
          {t('storylines.empty')}
        </div>
      ) : (
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
          <div className="space-y-3">
            {storylines.map((line) => {
              const lineSubplots = subplots.filter((item) => item.storyline_id === line.id)
              return (
                <Card key={line.id} className="rounded-lg shadow-xs">
                  <CardHeader className="space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <CardTitle className="truncate text-base">{line.name}</CardTitle>
                        <div className="mt-2 flex flex-wrap gap-2">
                          <Badge variant="outline">{t(`storylines.lineTypes.${line.line_type}`, { defaultValue: line.line_type })}</Badge>
                          <Badge variant="secondary">{t(`storylines.status.${line.status}`, { defaultValue: line.status })}</Badge>
                          <Badge variant="outline">{t('storylines.priority', { priority: line.priority })}</Badge>
                        </div>
                      </div>
                      <div className="flex shrink-0 gap-1">
                        <Button type="button" variant="ghost" size="icon" onClick={() => openSubplotCreate(line.id)}>
                          <Plus className="size-4" />
                          <span className="sr-only">{t('storylines.newSubplot')}</span>
                        </Button>
                        <Button type="button" variant="ghost" size="icon" onClick={() => openStorylineEdit(line)}>
                          <Pencil className="size-4" />
                          <span className="sr-only">{t('common.edit')}</span>
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          disabled={deleteStorylineMutation.isPending}
                          onClick={() => {
                            if (window.confirm(t('storylines.confirmDeleteStoryline', { name: line.name }))) {
                              deleteStorylineMutation.mutate(line.id)
                            }
                          }}
                        >
                          <Trash2 className="size-4" />
                          <span className="sr-only">{t('storylines.deleteStoryline')}</span>
                        </Button>
                      </div>
                    </div>
                    {line.arc_summary ? <p className="text-sm leading-6 text-muted-foreground">{line.arc_summary}</p> : null}
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {lineSubplots.length ? (
                      lineSubplots.map((item) => (
                        <div key={item.id} className="rounded-md border bg-background/40 p-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 space-y-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="truncate text-sm font-medium">{item.title}</p>
                                <Badge variant={item.status === 'detonated' ? 'secondary' : 'outline'}>
                                  {t(`storylines.subplotStatus.${item.status}`, { defaultValue: item.status })}
                                </Badge>
                              </div>
                              <p className="text-xs text-muted-foreground">
                                {t('storylines.subplotMeta', {
                                  dig: item.dig_chapter_number ?? '-',
                                  target: item.target_detonate_chapter_number ?? '-',
                                  convergence: item.convergence_chapter_number ?? '-',
                                })}
                              </p>
                              {item.description ? <p className="text-sm leading-6 text-muted-foreground">{item.description}</p> : null}
                            </div>
                            <div className="flex shrink-0 flex-wrap justify-end gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                disabled={item.status !== 'dug' || fillMutation.isPending}
                                onClick={() => fillMutation.mutate(item.id)}
                              >
                                <Sparkles className="size-4" />
                                <span className="sr-only">{t('storylines.fillSubplot')}</span>
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                disabled={item.status !== 'filling' || detonateMutation.isPending}
                                onClick={() => detonateMutation.mutate({ id: item.id, chapterNo: chapterNumber })}
                              >
                                <Zap className="size-4" />
                                <span className="sr-only">{t('storylines.detonateSubplot')}</span>
                              </Button>
                              <Button type="button" variant="ghost" size="icon" onClick={() => openSubplotEdit(item)}>
                                <Pencil className="size-4" />
                                <span className="sr-only">{t('common.edit')}</span>
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                disabled={deleteSubplotMutation.isPending}
                                onClick={() => {
                                  if (window.confirm(t('storylines.confirmDeleteSubplot', { title: item.title }))) {
                                    deleteSubplotMutation.mutate(item.id)
                                  }
                                }}
                              >
                                <Trash2 className="size-4" />
                                <span className="sr-only">{t('storylines.deleteSubplot')}</span>
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">{t('storylines.noSubplots')}</p>
                    )}
                  </CardContent>
                </Card>
              )
            })}
          </div>

          <Card className="h-fit rounded-lg shadow-xs">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <GitBranch className="size-4 text-primary" />
                {t('storylines.weightsTitle', { chapter: chapterNumber })}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {weightsQuery.isLoading ? (
                <Skeleton className="h-32 rounded-lg" />
              ) : (
                storylines.map((line) => {
                  const existing = weightsByStoryline.get(line.id)
                  const draft = weightDraft[line.id]
                  return (
                    <div key={line.id} className="space-y-2 rounded-md border bg-background/40 p-3">
                      <Label className="text-sm font-medium">{line.name}</Label>
                      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
                        <Input
                          type="number"
                          min="0"
                          max="1"
                          step="0.05"
                          value={draft?.weight ?? String(existing?.weight ?? 0)}
                          onChange={(event) =>
                            setWeightDraft((current) => ({
                              ...current,
                              [line.id]: {
                                weight: event.target.value,
                                is_convergence_node: draft?.is_convergence_node ?? existing?.is_convergence_node ?? false,
                              },
                            }))
                          }
                        />
                        <label className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Switch
                            checked={draft?.is_convergence_node ?? existing?.is_convergence_node ?? false}
                            onCheckedChange={(checked) =>
                              setWeightDraft((current) => ({
                                ...current,
                                [line.id]: {
                                  weight: draft?.weight ?? String(existing?.weight ?? 0),
                                  is_convergence_node: checked,
                                },
                              }))
                            }
                          />
                          {t('storylines.convergenceNode')}
                        </label>
                      </div>
                    </div>
                  )
                })
              )}
              <Button type="button" className="w-full" disabled={weightsMutation.isPending} onClick={saveWeights}>
                <Save className="size-4" />
                {t('storylines.saveWeights')}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      <Dialog open={storylineDialog} onOpenChange={setStorylineDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingStoryline ? t('storylines.editStoryline') : t('storylines.createStoryline')}</DialogTitle>
            <DialogDescription>{t('storylines.storylineDialogDescription')}</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitStoryline} className="space-y-4">
            <Field id="storyline-name" label={t('storylines.fields.name')} value={storylineForm.name} onChange={(value) => setStorylineForm((current) => ({ ...current, name: value }))} />
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">{t('storylines.fields.lineType')}</Label>
                <Select value={storylineForm.line_type} onValueChange={(value) => setStorylineForm((current) => ({ ...current, line_type: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {['main', 'romance', 'power', 'mystery', 'politics'].map((item) => (
                      <SelectItem key={item} value={item}>{t(`storylines.lineTypes.${item}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">{t('storylines.fields.priority')}</Label>
                <NumberInput value={storylineForm.priority} onChange={(value) => setStorylineForm((current) => ({ ...current, priority: value ?? 0 }))} />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">{t('storylines.fields.status')}</Label>
                <Select value={storylineForm.status} onValueChange={(value) => setStorylineForm((current) => ({ ...current, status: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {['active', 'dormant', 'closed'].map((item) => (
                      <SelectItem key={item} value={item}>{t(`storylines.status.${item}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Field id="storyline-summary" label={t('storylines.fields.arcSummary')} value={storylineForm.arc_summary} multiline onChange={(value) => setStorylineForm((current) => ({ ...current, arc_summary: value }))} />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setStorylineDialog(false)}>{t('common.cancel')}</Button>
              <Button type="submit">{t('common.save')}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={subplotDialog} onOpenChange={setSubplotDialog}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>{editingSubplot ? t('storylines.editSubplot') : t('storylines.createSubplot')}</DialogTitle>
            <DialogDescription>{t('storylines.subplotDialogDescription')}</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitSubplot} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">{t('storylines.fields.storyline')}</Label>
              <Select value={subplotForm.storyline_id} onValueChange={(value) => setSubplotForm((current) => ({ ...current, storyline_id: value }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {storylines.map((line) => (
                    <SelectItem key={line.id} value={line.id}>{line.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Field id="subplot-title" label={t('storylines.fields.title')} value={subplotForm.title} onChange={(value) => setSubplotForm((current) => ({ ...current, title: value }))} />
            <Field id="subplot-description" label={t('storylines.fields.description')} value={subplotForm.description} multiline onChange={(value) => setSubplotForm((current) => ({ ...current, description: value }))} />
            <Separator />
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">{t('storylines.fields.digChapter')}</Label>
                <NumberInput value={subplotForm.dig_chapter_number} onChange={(value) => setSubplotForm((current) => ({ ...current, dig_chapter_number: value }))} />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">{t('storylines.fields.targetChapter')}</Label>
                <NumberInput value={subplotForm.target_detonate_chapter_number} onChange={(value) => setSubplotForm((current) => ({ ...current, target_detonate_chapter_number: value }))} />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">{t('storylines.fields.convergenceChapter')}</Label>
                <NumberInput value={subplotForm.convergence_chapter_number} onChange={(value) => setSubplotForm((current) => ({ ...current, convergence_chapter_number: value }))} />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">{t('storylines.fields.importance')}</Label>
                <Input type="number" min="0" max="1" step="0.05" value={subplotForm.importance} onChange={(event) => setSubplotForm((current) => ({ ...current, importance: event.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">{t('storylines.fields.remindBefore')}</Label>
                <NumberInput value={subplotForm.remind_before_chapters} onChange={(value) => setSubplotForm((current) => ({ ...current, remind_before_chapters: value }))} />
              </div>
              <label className="flex items-center gap-2 self-end rounded-md border border-input px-3 py-2 text-sm">
                <Switch checked={subplotForm.include_in_context} onCheckedChange={(checked) => setSubplotForm((current) => ({ ...current, include_in_context: checked }))} />
                {t('storylines.fields.includeInContext')}
              </label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSubplotDialog(false)}>{t('common.cancel')}</Button>
              <Button type="submit">{t('common.save')}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
